import { NECTAR_API_URL } from '@/env'
import { useFeatureFlag } from '@/featureFlags'
import { analytics } from '@/features/analytics/events'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SkipReasonCode } from '../components/free-speech/skipReasons'
import { getAccessGateStatusForEnrollment, getRandomAccessGatePhrase, submitAccessGateCheck } from './access-gate'
import { claimCampaignOnce } from './campaignClaim'
import { nectarApi } from './client'
import type {
  AnnotatorPretestResult,
  AnnotatorPretestTaskSubmission,
  EnrollResponse,
  NectarProject,
  NectarTask,
  NextTaskResponse,
  PaginatedResponse,
  SkipResponse
} from './models'
import { getAssessmentsByLanguage } from './proficiency-assessment'

const useNectarEnabled = () => {
  const flagEnabled = useFeatureFlag('nectarMission')
  return !!NECTAR_API_URL && flagEnabled
}

export const useNectarProjects = () => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_PROJECTS'],
    queryFn: () => nectarApi.get<PaginatedResponse<NectarProject>>('/v1/projects', { page: 1, limit: 50 }),
    staleTime: 50_000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled
  })
}
export const useNectarProjectsUnauthorized = () => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_PROJECTS_UNAUTHORIZED'],
    queryFn: () => nectarApi.get<PaginatedResponse<NectarProject>>('/v1/public/projects', { page: 1, limit: 50 }),
    select: (response) => ({
      ...response,
      data: response.data.map((project) => ({ ...project, availability: { available: true, reasons: [] } }))
    }),
    staleTime: 50_000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled
  })
}

export const useNectarProject = (projectId: string | undefined) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_PROJECT', projectId],
    // Claim ad-campaign access (if a utm_campaign was captured) BEFORE loading the project, so an
    // invite-only project's whitelist gate is already passed on this first read.
    queryFn: async () => {
      await claimCampaignOnce(projectId as string)
      return nectarApi.get<NectarProject>(`/v1/projects/${projectId}`)
    },
    refetchOnWindowFocus: false,
    enabled: enabled && !!projectId
  })
}

export const useNectarProjectUnauthorized = (projectId: string | undefined) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_PROJECT_UNAUTHORIZED', projectId],
    queryFn: () => nectarApi.get<NectarProject>(`/v1/public/projects/${projectId}`),
    refetchOnWindowFocus: false,
    enabled: enabled && !!projectId
  })
}

export const useLinkedNectarProjects = (projectId: string | undefined) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_PROJECT_LINKED', projectId],
    queryFn: () => nectarApi.get<{ data: NectarProject[] }>(`/v1/projects/${projectId}/linked`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled: enabled && !!projectId
  })
}

export const useNectarTasks = (projectId: string | undefined) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_TASKS', projectId],
    queryFn: () =>
      nectarApi.get<PaginatedResponse<NectarTask>>(`/v1/projects/${projectId}/tasks`, { page: 1, limit: 100 }),
    refetchOnWindowFocus: false,
    enabled: enabled && !!projectId
  })
}

export const useNectarTask = (projectId: string | undefined, taskId: string | undefined) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_TASK', projectId, taskId],
    queryFn: () => nectarApi.get<NectarTask>(`/v1/projects/${projectId}/tasks/${taskId}`),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    enabled: enabled && !!projectId && !!taskId
  })
}

export const useEnrollInProject = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, acceptRules }: { projectId: string; acceptRules?: boolean }) =>
      nectarApi.post<EnrollResponse>(
        `/v1/projects/${projectId}/enroll`,
        acceptRules ? { acceptRules: true } : undefined
      ),
    onSuccess: async ({ projectId }) => {
      analytics.missionOnboardingStepCTA(projectId, 'Start Mission')
      await queryClient.refetchQueries({ queryKey: ['NECTAR_PROJECTS'] })
      await queryClient.refetchQueries({ queryKey: ['NECTAR_PROJECT', projectId] })
    }
  })
}

export const useSubmitNectarTask = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      projectId,
      taskId,
      data,
      turnstileToken
    }: {
      projectId: string
      taskId: string
      data: FormData | Record<string, unknown>
      turnstileToken?: string
    }) =>
      nectarApi.post(
        `/v1/projects/${projectId}/tasks/${taskId}/submissions`,
        data,
        turnstileToken ? { headers: { 'X-Turnstile-Token': turnstileToken } } : undefined
      ),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['NECTAR_TASKS', variables.projectId] }),
        queryClient.invalidateQueries({ queryKey: ['NECTAR_NEXT_TASK', variables.projectId] }),
        queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECT', variables.projectId] }),
        queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECTS'] })
      ])
    }
  })
}

export const useNextTask = (projectId: string | undefined) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_NEXT_TASK', projectId],
    queryFn: () => nectarApi.get<NextTaskResponse>(`/v1/projects/${projectId}/tasks/next`),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: enabled && !!projectId
  })
}

export const useProficiencyAssessmentsByLanguage = (language: string, options?: { enabled?: boolean }) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_PROFICIENCY_ASSESSMENTS', language],
    queryFn: () => getAssessmentsByLanguage(language),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: enabled && !!language && options?.enabled !== false
  })
}

export const useAccessGatePhrase = (language: string | null | undefined, options?: { enabled?: boolean }) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_ACCESS_GATE_PHRASE', language],
    queryFn: () => getRandomAccessGatePhrase(language as string),
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled && !!language && options?.enabled !== false
  })
}

export const useAccessGateStatus = (enrollmentId: string | null | undefined, options?: { enabled?: boolean }) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_ACCESS_GATE_STATUS', enrollmentId],
    queryFn: () => getAccessGateStatusForEnrollment(enrollmentId as string),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled && !!enrollmentId && options?.enabled !== false
  })
}

export const useSubmitAccessGateCheck = () => {
  return useMutation({
    mutationFn: ({ enrollmentId, phraseId, blob }: { enrollmentId: string; phraseId: string | null; blob: Blob }) => {
      const type = blob.type || 'audio/webm'
      const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : type.includes('wav') ? 'wav' : 'webm'
      const file = new File([blob], `access-gate.${ext}`, { type })
      const formData = new FormData()
      formData.append('file', file)
      formData.append('enrollmentId', enrollmentId)
      if (phraseId) formData.append('phraseId', phraseId)
      return submitAccessGateCheck(formData)
    }
  })
}

export const sendTaskHeartbeat = (projectId: string, taskId: string, visible: boolean) =>
  nectarApi.post<{ accepted: boolean; activeMs: number }>(`/v1/projects/${projectId}/tasks/${taskId}/heartbeat`, {
    visible
  })

export type AccessRequestInfo = {
  eligible: true
  gated: true
  requirements: {
    profileComplete: boolean
    languageAssessmentComplete: boolean
  }
  status: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
}

export const useProjectAccessRequestInfo = (projectId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['NECTAR_ACCESS_REQUEST_INFO', projectId],
    // skipAuthRedirect: a 401 here (logged-out / stale token) must NOT bounce the page to /login —
    // it falls back to the default status and the component shows the sign-in CTA instead.
    queryFn: () =>
      nectarApi.get<AccessRequestInfo>(`/v1/projects/${projectId}/access-request`, undefined, {
        skipAuthRedirect: true
      }),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    // Authenticated endpoint — also gate it so it doesn't even fire while logged out.
    enabled: (options?.enabled ?? true) && !!projectId
  })
}

export const useRequestProjectAccess = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (projectId: string) => nectarApi.post<void>(`/v1/projects/${projectId}/access-request`, {}),
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['NECTAR_ACCESS_REQUEST_INFO', projectId] })
    }
  })
}

export const useAnnotatorVerificationTasks = (
  projectId: string | undefined,
  count: number,
  options?: { enabled?: boolean }
) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_ANNOTATOR_VERIFICATION_TASKS', projectId, count],
    queryFn: () =>
      nectarApi.get<NectarTask[]>(`/v1/projects/${projectId}/tasks/golden/random`, { count }),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled && !!projectId && options?.enabled !== false
  })
}

export const useSubmitAnnotatorPretest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, tasks }: { projectId: string; tasks: AnnotatorPretestTaskSubmission[] }) =>
      nectarApi.post<AnnotatorPretestResult>(`/v1/projects/${projectId}/pretest`, { tasks }),
    onSuccess: async (_data, { projectId }) => {
      analytics.missionOnboardingStepCTA(projectId, 'Annotator Verification')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECT', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECTS'] })
      ])
    }
  })
}

export const useReportTask = () => {
  return useMutation({
    mutationFn: ({
      projectId,
      taskId,
      userId,
      reason
    }: {
      projectId: string
      taskId: string
      userId: string
      reason: string
    }) => nectarApi.post<void>(`/v1/tasks/${projectId}/tasks/${taskId}/report`, { userId, projectId, reason })
  })
}

export const useSkipFreeSpeechTask = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, taskId, code }: { projectId: string; taskId: string; code: SkipReasonCode }) =>
      nectarApi.post<SkipResponse>(`/v1/projects/${projectId}/tasks/${taskId}/skip`, { code }),
    onSuccess: async (_data, { projectId }) => {
      await queryClient.invalidateQueries({ queryKey: ['NECTAR_NEXT_TASK', projectId] })
    }
  })
}
